// ============================================================
// AuthContext — wraps @square/core/auth
//
// Honest contract:
//   - On mount, calls getCurrentUser() to hydrate from any
//     existing Supabase session (persisted in localStorage by
//     supabase-js).
//   - Subscribes to onAuthChange so multiple tabs stay in sync.
//   - Exposes signIn / signOut so routes don't reach into
//     @square/core directly.
//   - After every successful sign-in (and on initial hydration
//     when a user is already present), fires /api/auth/bootstrap
//     to ensure olv_sessions + sq_organizations + sq_memberships
//     are populated. This is the bridge between Supabase Auth
//     and the OLIVIUM trust layer.
//   - After bootstrap, runs /api/auth/allowlist-check to verify
//     the user is on the approved operator list.
//
// Server-only auth (verifyAccessToken) is not surfaced here —
// that's used by Vercel functions, not browser code.
// ============================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  signOut as coreSignOut,
  getCurrentUser,
  onAuthChange,
  tryCreateBrowserClient,
  type AuthUser,
  type SignInResult,
} from '@square/core'

import { api, ApiError, type BootstrapResult, type AllowlistResult } from '../api/client'

interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  /** The most recent /api/auth/bootstrap result, or null. */
  bootstrap: BootstrapResult | null
  /** True if the role is loaded and level >= 80. */
  isAdmin: boolean
  /** Allowlist gate result — null while checking, true if approved, false if denied. */
  approved: boolean | null
  /** The allowlist check result details, or null. */
  allowlist: AllowlistResult | null
  /** Diagnostic: which auth step is currently running. */
  authStep: string
  signIn: (email: string, password: string) => Promise<SignInResult>
  signOut: () => Promise<{ ok: boolean; error: string | null }>
  /** Manually re-run /api/auth/bootstrap. Idempotent. */
  refreshBootstrap: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

/** Max time (ms) to wait for bootstrap + allowlist before failing visible. */
const GATE_TIMEOUT_MS = 15_000

/**
 * Browser-only device fingerprint. We don't ship a real
 * fingerprinting library — we just hash the UA + screen so the
 * server can distinguish two browsers without tracking the user
 * across sites. The server falls back to a UA-only fingerprint
 * if this is missing.
 */
function deviceFingerprint(): string | undefined {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return undefined
  try {
    const seed = `${navigator.userAgent}|${window.screen?.width || 0}x${window.screen?.height || 0}`
    let h = 0
    for (let i = 0; i < seed.length; i++) {
      h = (h * 31 + seed.charCodeAt(i)) | 0
    }
    return `dev_${(h >>> 0).toString(16)}`
  } catch {
    return undefined
  }
}

function log(step: string, detail?: unknown) {
  // eslint-disable-next-line no-console
  console.log(`[AuthContext] ${step}`, detail ?? '')
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [bootstrap, setBootstrap] = useState<BootstrapResult | null>(null)
  const [allowlist, setAllowlist] = useState<AllowlistResult | null>(null)
  const [approved, setApproved] = useState<boolean | null>(null)
  const [authStep, setAuthStep] = useState('init')
  const lastBootstrappedUser = useRef<string | null>(null)
  const gateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Safety timeout: if approved stays null for too long, fail visible.
  const startGateTimeout = useCallback(() => {
    if (gateTimerRef.current) clearTimeout(gateTimerRef.current)
    gateTimerRef.current = setTimeout(() => {
      setApproved((prev) => {
        if (prev === null) {
          log('gate-timeout', 'approved still null after timeout, failing to denied')
          setAuthStep('gate-timeout')
          return false
        }
        return prev
      })
    }, GATE_TIMEOUT_MS)
  }, [])

  const clearGateTimeout = useCallback(() => {
    if (gateTimerRef.current) {
      clearTimeout(gateTimerRef.current)
      gateTimerRef.current = null
    }
  }, [])

  const runAllowlistCheck = useCallback(async () => {
    log('allowlist-start')
    setAuthStep('allowlist-check')
    try {
      const result = await api.auth.allowlistCheck()
      log('allowlist-result', { approved: result.approved, source: result.source })
      setAllowlist(result)
      setApproved(result.approved)
      setAuthStep(result.approved ? 'approved' : 'denied')
      clearGateTimeout()
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.code}: ${err.message}` : (err as Error).message
      log('allowlist-failed', msg)
      // eslint-disable-next-line no-console
      console.warn('[AuthContext] allowlist check failed:', msg)
      setApproved(false)
      setAuthStep('allowlist-error')
      clearGateTimeout()
    }
  }, [clearGateTimeout])

  const runBootstrap = useCallback(async () => {
    log('bootstrap-start')
    setAuthStep('bootstrap')
    try {
      const result = await api.auth.bootstrap(deviceFingerprint())
      log('bootstrap-ok', { org_id: result.org_id, role: result.role?.name })
      setBootstrap(result)
    } catch (err) {
      const msg = err instanceof ApiError ? `${err.code}: ${err.message}` : (err as Error).message
      log('bootstrap-failed', msg)
      // eslint-disable-next-line no-console
      console.warn('[AuthContext] bootstrap failed:', msg)
    }
    // Always run the allowlist check, even if bootstrap failed.
    await runAllowlistCheck()
  }, [runAllowlistCheck])

  // Hydrate the current Supabase session and run bootstrap once.
  useEffect(() => {
    let cancelled = false
    log('hydrate-start')
    setAuthStep('hydrating')

    getCurrentUser()
      .then(async (u) => {
        if (cancelled) return
        log('hydrate-user', u ? { id: u.id, email: u.email } : 'no user')
        setUser(u)
        if (u && lastBootstrappedUser.current !== u.id) {
          lastBootstrappedUser.current = u.id
          startGateTimeout()
          await runBootstrap()
        }
      })
      .catch((err) => {
        log('hydrate-error', (err as Error).message)
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
          log('loading-false')
        }
      })

    const unsubscribe = onAuthChange(async (u) => {
      if (cancelled) return
      log('auth-change', u ? { id: u.id, email: u.email } : 'signed out')
      setUser(u)
      if (!u) {
        // Sign-out: clear bootstrap + allowlist state.
        setBootstrap(null)
        setAllowlist(null)
        setApproved(null)
        setAuthStep('signed-out')
        lastBootstrappedUser.current = null
        clearGateTimeout()
        return
      }
      if (lastBootstrappedUser.current !== u.id) {
        lastBootstrappedUser.current = u.id
        startGateTimeout()
        await runBootstrap()
      }
    })

    return () => {
      cancelled = true
      unsubscribe()
      clearGateTimeout()
    }
  }, [runBootstrap, startGateTimeout, clearGateTimeout])

  const handleSignIn = useCallback(
    async (email: string, password: string): Promise<SignInResult> => {
      try {
        const result = await api.auth.login(email, password, deviceFingerprint())
        if (!result.ok || !result.session?.access_token) {
          return { ok: false, user: null, error: 'Login failed' }
        }

        const supabase = tryCreateBrowserClient()
        if (supabase) {
          await supabase.auth.setSession({
            access_token: result.session.access_token,
            refresh_token: result.session.refresh_token,
          })
        }

        const authUser: AuthUser = {
          id: result.user.id,
          email: result.user.email ?? null,
        }
        setUser(authUser)

        lastBootstrappedUser.current = authUser.id
        startGateTimeout()
        await runBootstrap()

        return { ok: true, user: authUser, error: null }
      } catch (err) {
        const apiErr = err as ApiError
        return {
          ok: false,
          user: null,
          error: apiErr?.message || 'Login failed',
        }
      }
    },
    [runBootstrap, startGateTimeout],
  )

  const handleSignOut = useCallback(
    async (): Promise<{ ok: boolean; error: string | null }> => {
      try {
        await api.auth.logout()
      } catch {
        // Best-effort — continue with browser sign-out even if server fails.
      }
      return coreSignOut()
    },
    [],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      bootstrap,
      isAdmin: !!bootstrap?.role && bootstrap.role.level >= 80,
      approved,
      allowlist,
      authStep,
      signIn: handleSignIn,
      signOut: handleSignOut,
      refreshBootstrap: runBootstrap,
    }),
    [user, loading, bootstrap, approved, allowlist, authStep, handleSignIn, handleSignOut, runBootstrap],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be called inside <AuthProvider>')
  }
  return ctx
}

/**
 * Provider-optional read of the auth context. Returns null instead of throwing
 * when there is no <AuthProvider> above (SSR / provider-light tests). Use this
 * for full-screen surfaces that render in both provider and non-provider
 * contexts and must degrade honestly (unknown auth -> gated), never crash.
 */
export function useAuthOptional(): AuthContextValue | null {
  return useContext(AuthContext)
}
